import { HTMLAttributes } from 'react';
import { PaginationLink } from './PaginationLink';
import styles from './styles.module.scss';

export type PaginationProps = HTMLAttributes<HTMLElement> & {
  currentPage: number;
  pageCount: number;
  maxLinksDisplayed?: number;
  fnSetPageNum?: (page: number) => void;
};

const calcDisplayedPages = (pageCount: number, maxLinksDisplayed: number, currentPage: number) => {
  if (pageCount <= maxLinksDisplayed) {
    return Array.from({ length: pageCount }, (value, key) => key + 1);
  }

  const delta = Math.floor(maxLinksDisplayed / 2);
  let first = currentPage - delta - 1 >= 0 ? currentPage - delta - 1 : 0;
  let last = currentPage + delta <= pageCount ? currentPage + delta : pageCount;

  if (delta + currentPage >= pageCount) {
    first -= delta + currentPage - pageCount;
  }
  if (delta - currentPage >= 0) {
    last += delta - currentPage + 1;
  }

  return Array.from({ length: pageCount }, (value, key) => key + 1).slice(first, last);
};

export const Pagination = ({
  currentPage,
  pageCount,
  maxLinksDisplayed = 5,
  fnSetPageNum,
}: PaginationProps) => {
  const displayedPages = calcDisplayedPages(pageCount, maxLinksDisplayed, currentPage);

  const handlePageChange = fnSetPageNum || (() => undefined);

  return (
    <nav aria-label="Page navigation" className={styles.paginationContainer}>
      <ul className={styles.paginationList}>
        <PaginationLink page={currentPage - 1} onPageChange={handlePageChange} disabled={currentPage === 1}>
          Prev Page
        </PaginationLink>
        {displayedPages.map((pageNum, index) => (
          <PaginationLink
            key={index.toString()}
            page={pageNum}
            onPageChange={handlePageChange}
            active={currentPage === pageNum}
            variant="circle"
          >
            {pageNum}
          </PaginationLink>
        ))}
        <PaginationLink
          page={currentPage + 1}
          onPageChange={handlePageChange}
          disabled={currentPage === pageCount}
        >
          Next Page
        </PaginationLink>
      </ul>
    </nav>
  );
};

export { PaginationLink };

Pagination.displayName = 'Pagination';
